import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { LockOutlined as LockIcon } from '@mui/icons-material';

/**
 * Component to handle force change password challenge from AWS Cognito
 * This appears when a new user logs in for the first time
 */
const ForceChangePassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Password requirements
  const PASSWORD_MIN_LENGTH = 8;
  const PASSWORD_REQUIRES_UPPERCASE = true;
  const PASSWORD_REQUIRES_LOWERCASE = true;
  const PASSWORD_REQUIRES_NUMBER = true;
  const PASSWORD_REQUIRES_SPECIAL = true;

  const validatePassword = (): boolean => {
    // Clear previous errors
    setError(null);
    
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    // Check password length
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
      return false;
    }
    
    // Check for uppercase letter
    if (PASSWORD_REQUIRES_UPPERCASE && !/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    
    // Check for lowercase letter
    if (PASSWORD_REQUIRES_LOWERCASE && !/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    
    // Check for number
    if (PASSWORD_REQUIRES_NUMBER && !/\d/.test(newPassword)) {
      setError('Password must contain at least one number');
      return false;
    }
    
    // Check for special character
    if (PASSWORD_REQUIRES_SPECIAL && !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setError('Password must contain at least one special character');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    if (!validatePassword()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const success = await authService.completeNewPasswordChallenge(newPassword);
      
      if (success) {
        // Password change successful, redirect to dashboard
        navigate('/dashboard');
      } else {
        setError('Failed to change password. Please try again or contact support.');
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no password change required, redirect to dashboard
  React.useEffect(() => {
    if (!authService.isPasswordChangeRequired() && authService.isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            borderRadius: '50%', 
            p: 1, 
            mb: 2 
          }}>
            <LockIcon fontSize="large" />
          </Box>
          
          <Typography component="h1" variant="h5" gutterBottom>
            Change Your Password
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            You need to change your password before continuing.
            Please create a strong password that meets all requirements.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Change Password'
              )}
            </Button>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Password must:
            </Typography>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>
                <Typography variant="caption" color="text.secondary">
                  Be at least {PASSWORD_MIN_LENGTH} characters long
                </Typography>
              </li>
              {PASSWORD_REQUIRES_UPPERCASE && (
                <li>
                  <Typography variant="caption" color="text.secondary">
                    Include at least one uppercase letter
                  </Typography>
                </li>
              )}
              {PASSWORD_REQUIRES_LOWERCASE && (
                <li>
                  <Typography variant="caption" color="text.secondary">
                    Include at least one lowercase letter
                  </Typography>
                </li>
              )}
              {PASSWORD_REQUIRES_NUMBER && (
                <li>
                  <Typography variant="caption" color="text.secondary">
                    Include at least one number
                  </Typography>
                </li>
              )}
              {PASSWORD_REQUIRES_SPECIAL && (
                <li>
                  <Typography variant="caption" color="text.secondary">
                    Include at least one special character (!@#$%^&*...)
                  </Typography>
                </li>
              )}
            </ul>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForceChangePassword; 